import type { PageHelp } from "../../help/types";
import {
  IMPLEMENTATION_CONSENT_CONTEXTS,
  IMPLEMENTATION_DIALOG_BENEFIT,
  IMPLEMENTATION_DIALOG_POLICY,
  implementationDialogSteps,
} from "../CompanyBenefitImplementationPage/help-steps";
import {
  DOCUMENTS_CONTEXTS,
  DOCUMENTS_DIALOG,
  LIST_VIEW_CONTEXT,
  documentsDialogSteps,
} from "../CompanyPackagesPage/help-steps";

const CHOOSING = "Choosing a company";
const TOOLBAR = "Tabs and toolbar";
const READING_CARDS = "Reading the category cards";
const CARD_ACTIONS = "Working with a category card";
const LIST_VIEW = "List view";
const FIRST_SETUP = "First setup of a client";
const CATEGORY_DIALOG = "Category dialog";
const CLIENT = "Client menu and client dialog";

export const companyProfilePageHelp: PageHelp = {
  title: "Company profile",
  intro:
    "The starting point for setting up a client's benefits. Pick a country and a company to see the benefit categories the client offers, open a category to manage its implementations and packages, and maintain the client's texts and documents for employees.",
  sections: [
    CHOOSING,
    TOOLBAR,
    READING_CARDS,
    CARD_ACTIONS,
    LIST_VIEW,
    FIRST_SETUP,
    CATEGORY_DIALOG,
    CLIENT,
    IMPLEMENTATION_DIALOG_BENEFIT,
    IMPLEMENTATION_DIALOG_POLICY,
    DOCUMENTS_DIALOG,
  ],
  contexts: {
    "list-view": LIST_VIEW_CONTEXT,
    "select-categories":
      "Opens by itself when you select a company that has no benefit categories in the chosen country yet. If you closed it, select the company again.",
    "category-dialog":
      'Click "Add Benefit Category" in the toolbar, or the edit icon on a category card or table row.',
    "category-dialog-add": 'Click "Add Benefit Category" in the toolbar.',
    "category-dialog-edit": "Click the edit icon on a category card or table row.",
    "client-menu": "Click the client name with the logo in the toolbar.",
    "client-dialog": 'Click the client name in the toolbar and choose "Edit".',
    "implementation-dialog":
      'Click the + on a category card ("Add New Implementation"), or "Implement Benefit" in a table row.',
    "implementation-dialog-add":
      'Click the + on a category card ("Add New Implementation"), or "Implement Benefit" in a table row.',
    ...IMPLEMENTATION_CONSENT_CONTEXTS,
    "documents-dialog":
      "Click the documents icon on a category card, the Documents cell of a table row, or the documents button in the category or client dialog.",
    ...DOCUMENTS_CONTEXTS,
  },
  tips: [
    "The structure is always client → benefit category → implementation (insurer, policy, validity) → package (price, eligibility). Each level has its own page; the breadcrumb on the deeper pages leads back here.",
    "Client categories are copies of the country's categories. Their names are fixed here; consent, auto-enrollment and description can be adjusted per client.",
    "Grid view (cards) is the default. Switch to list view in the settings menu (gear icon in the header) to work with the categories in a table.",
    "Read-only roles see the same pages without the editing controls. Deleting a client, category, implementation or package is carried out only for GrECo global administrators.",
  ],
  steps: [
    {
      selector: '[data-help="subheader-country-select"]',
      section: CHOOSING,
      title: "Choose the country",
      body: "Lists the countries your role gives you access to; type to filter. Changing the country clears the selected company, because company search works within one country.",
    },
    {
      selector: '[data-help="subheader-client-select"]',
      section: CHOOSING,
      title: "Find the company",
      body: 'Type at least one letter of the company name and pick it from the results, shown as "name (partner number)". Disabled until a country is chosen. The company stays selected when you come back to this page, and the HR page uses the same company.',
    },
    {
      selector: '[data-help="subheader-refresh"]',
      section: CHOOSING,
      title: "Reload the page data",
      body: "On the Benefits tab it reloads the benefit categories of the company; on the Analytics tab it reloads the report. Disabled until a country and a company are selected.",
    },
    {
      selector: '[data-help="subheader-search"]',
      section: CHOOSING,
      title: "Search categories by name",
      body: "Filters the category cards or rows to names containing the text; the number at the right end of the bar shows how many match. Disabled until the company has categories.",
    },
    {
      selector: '[data-help="company-tab-benefits"]',
      section: TOOLBAR,
      optional: true,
      title: "Show the benefit categories",
      body: "The Benefits tab lists the benefit categories of the selected company. The tabs and the rest of the toolbar appear once the company has at least one category.",
    },
    {
      selector: '[data-help="company-tab-analytics"]',
      section: TOOLBAR,
      optional: true,
      title: "Show the analytics report",
      body: "The Analytics tab shows the Power BI report for the selected company and country. Use the refresh button next to the country field to reload it.",
    },
    {
      selector: '[data-help="benefit-cards-save-order"]',
      section: TOOLBAR,
      optional: true,
      title: "Save your card order",
      body: 'In grid view you can drag the category cards into any order; "Save Order" stores it for your user and this company only. Disabled while a search is active.',
    },
    {
      selector: '[data-help="edit-client-menu"]',
      section: TOOLBAR,
      optional: true,
      title: "Open the client menu",
      body: 'Shows the logo and name of the client. Click the name to open a menu with "Edit" and "Delete"; click the logo to open the picture in a new tab.',
    },
    {
      selector: '[data-help="company-add-category"]',
      section: TOOLBAR,
      optional: true,
      title: "Add a benefit category to the client",
      body: '"Add Benefit Category" opens the category dialog, where you pick one of the country\'s categories the client does not have yet. Only admin roles can use it; for read-only roles it is disabled.',
    },
    {
      selector: '[data-help="company-categories"]',
      section: READING_CARDS,
      optional: true,
      title: "See the client's benefit categories",
      body: "Under \"Benefit Categories\" you find one card (or table row) per category the client offers, such as health or life insurance. Each category holds the client's implementations of that benefit.",
    },
    {
      selector: '[data-help="benefit-card"]',
      section: READING_CARDS,
      optional: true,
      title: "Read a category card",
      body: "Each card shows the category picture, name and description (hover to read it in full). Double-click the picture to open the implementations of the category. Drag a card to reorder the list, then save the order in the toolbar.",
    },
    {
      selector: '[data-help="benefit-card-tags"]',
      section: READING_CARDS,
      optional: true,
      title: "See how many benefits are implemented",
      body: "The ribbon tag shows the number of implementations in this category. Zero means the category is offered but not yet set up with an insurer and policy.",
    },
    {
      selector: '[data-help="benefit-card-open"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Open the implementations of a category",
      body: '"Open Benefits" goes to the implementation page of this category, where insurers, policies and validity periods are managed.',
    },
    {
      selector: '[data-help="benefit-card-add"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Implement a benefit from the card",
      body: 'The + on the card picture ("Add New Implementation") opens the implementation dialog for this category without leaving the page. Name, consent and auto-enrollment start with the category values.',
    },
    {
      selector: '[data-help="benefit-card-documents"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Manage the documents of a category",
      body: "Opens the documents dialog of this category for the client. Category documents have no employee-visibility switch. Changes there are saved immediately.",
    },
    {
      selector: '[data-help="benefit-card-edit"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Edit a category for this client",
      body: "Opens the category dialog. The name is fixed; consent, auto-enrollment and description can be changed for this client.",
    },
    {
      selector: '[data-help="benefit-card-delete"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "superadmin",
      title: "Delete a category from the client",
      body: "Deletes the category for this client after you confirm, together with its documents, implementations, packages and their employee enrollments. The server carries out deletes only for GrECo global administrators.",
    },
    {
      selector: '[data-help="benefit-category-column-name"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Sort the category table",
      body: "List view shows one row per category. Click any column header to sort by it. The names are read-only here because they come from the country categories.",
    },
    {
      selector: '[data-help="benefit-category-column-implement"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Implement a benefit from a row",
      body: '"Implement Benefit" opens the implementation dialog for the category in that row. Disabled for read-only roles.',
    },
    {
      selector: '[data-help="benefit-category-column-implemented"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "See the number of implementations",
      body: '"Implemented Benefits" counts the implementations of each category. Use the open icon in the Actions column to see them.',
    },
    {
      selector: '[data-help="benefit-category-column-documents"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Open the documents of a row",
      body: "The Documents cell shows how many files the category has and opens the documents dialog. Disabled for read-only roles.",
    },
    {
      selector: '[data-help="benefit-category-column-consent"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Change the consent default in the table",
      body: '"Consent Needed" is the starting value for new implementations of the category. Admin roles can change it in the cell; then save the row in the Actions column.',
    },
    {
      selector: '[data-help="benefit-category-column-auto-enrollment"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Change the auto-enrollment default in the table",
      body: '"Is Auto Enrollment" is the starting value for new implementations. It cannot be Yes together with "Consent Needed"; save the row in the Actions column.',
    },
    {
      selector: '[data-help="benefit-category-column-description"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Edit a description in the table",
      body: "Admin roles can change the client-specific description directly in the cell, then save the row in the Actions column.",
    },
    {
      selector: '[data-help="benefit-category-column-actions"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Use the row actions",
      body: "Edit opens the category dialog, the open icon goes to the implementations, and delete removes the category with everything below it. After you change a cell, Save and Undo appear here. Deletes are carried out only for GrECo global administrators.",
    },
    {
      selector: '[data-help="select-benefit-categories-table"]',
      section: FIRST_SETUP,
      context: "select-categories",
      title: "Choose the benefits the client offers",
      body: '"Add Benefits for" lists the benefit categories defined for the country. Tick "Included" for each category this client should offer; you can add more later with "Add Benefit Category".',
    },
    {
      selector: '[data-help="select-benefit-categories-select-all"]',
      section: FIRST_SETUP,
      context: "select-categories",
      title: "Include all categories",
      body: '"Select All" ticks every category in the list. Untick the ones the client does not need before saving.',
    },
    {
      selector: '[data-help="select-benefit-categories-clear-all"]',
      section: FIRST_SETUP,
      context: "select-categories",
      title: "Start from an empty selection",
      body: '"Clear all" unticks every category, so you can tick only the few the client needs.',
    },
    {
      selector: '[data-help="select-benefit-categories-save"]',
      section: FIRST_SETUP,
      context: "select-categories",
      title: "Create the client's categories",
      body: "Creates the ticked categories for the client as copies linked to the country categories, then shows them on the page. Closing the dialog with X instead leaves the client without categories.",
    },
    {
      selector: '[data-help="benefit-category-dialog-select"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog-add",
      title: "Pick the country category to add",
      body: '"Category" lists the country\'s benefit categories that this client does not have yet. Picking one copies its name, consent and auto-enrollment settings, picture and description into the form. Required.',
    },
    {
      selector: '[data-help="benefit-category-dialog-name"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog-edit",
      title: "Check the category name",
      body: "The name is inherited from the country category and cannot be changed on the Company profile. Ask a country or global administrator if it needs renaming.",
    },
    {
      selector: '[data-help="benefit-category-dialog-consent"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog",
      title: "Set the consent default",
      body: 'New implementations of this category start with this "Consent Needed" value. Choosing Yes sets "Is Auto Enrollment" to No.',
    },
    {
      selector: '[data-help="benefit-category-dialog-auto-enrollment"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog",
      title: "Set the auto-enrollment default",
      body: 'New implementations of this category start with this value. Disabled while "Consent Needed" is Yes.',
    },
    {
      selector: '[data-help="benefit-category-dialog-description"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog",
      title: "Describe the category for this client",
      body: "The description shown on the category card. Required, 2 to 4000 characters.",
    },
    {
      selector: '[data-help="benefit-category-dialog-attachments"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog-add",
      title: "Attach documents to the new category",
      body: 'Click "Upload" to add files that are saved together with the new category. The bin icon drops a file from the list before saving.',
    },
    {
      selector: '[data-help="benefit-category-dialog-documents"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog-edit",
      title: "Manage the documents of the category",
      body: 'Opens the documents dialog of the category. Changes there are saved immediately, not with the "Save" button of this form.',
    },
    {
      selector: '[data-help="benefit-category-dialog-save"]',
      section: CATEGORY_DIALOG,
      context: "category-dialog",
      title: "Save the category",
      body: "Adds the category to the client, or saves your changes, and closes the dialog. The new category appears as a card with zero implementations.",
    },
    {
      selector: '[data-help="edit-client-menu-edit"]',
      section: CLIENT,
      context: "client-menu",
      title: "Edit the client settings",
      body: '"Edit" opens the client dialog with the texts, pictures and documents employees see. Read-only roles can open it but cannot change or save anything.',
    },
    {
      selector: '[data-help="edit-client-menu-delete"]',
      section: CLIENT,
      context: "client-menu",
      title: "Delete the client from HBH",
      body: "Deletes the client after you confirm, with its benefit categories, implementations, packages, enrollments, employees, documents and user assignments. Active only for SuperAdmin; the server carries out deletes only for GrECo global administrators.",
    },
    {
      selector: '[data-help="edit-client-dialog-name"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Check the client name",
      body: '"Client Name" comes from GrECo\'s client records and cannot be changed in HBH.',
    },
    {
      selector: '[data-help="edit-client-dialog-sic-code"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Set the industry code",
      body: '"Sic Code" is the industry classification of the client. Required; type to filter the list.',
    },
    {
      selector: '[data-help="edit-client-dialog-cover"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Upload the company picture",
      body: '"Choose Company Cover Picture" sets the client logo or cover picture, shown next to the client name in the toolbar.',
    },
    {
      selector: '[data-help="edit-client-dialog-salary-consent"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Upload the salary deduction consent",
      body: '"Salary Deduction Consent" is the client\'s consent document for paying a package through salary deduction. Employees get it when they enroll in such a package in the employee app.',
    },
    {
      selector: '[data-help="edit-client-dialog-message"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Write the message to employees",
      body: '"Message To Employees" is the greeting shown at the top of the employee app for this client. Required, 2 to 4000 characters.',
    },
    {
      selector: '[data-help="edit-client-dialog-email-text"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Write the invitation e-mail",
      body: '"Email Invitation Text" is the body of the invitation e-mail HBH sends to the client\'s employees. Required, 2 to 4000 characters. "Clear" empties the editor; when it is empty, "Default" brings back the saved text.',
    },
    {
      selector: '[data-help="edit-client-dialog-welcome-text"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Design the welcome page",
      body: '"Welcome Page Content" is the formatted home page employees see in the employee app; it can include pictures, and the preview button shows how it will look. Required, up to about 4 MB of content.',
    },
    {
      selector: '[data-help="edit-client-dialog-documents"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Manage the client documents",
      body: "Opens the documents dialog for documents that belong to the client as a whole rather than to one benefit. Changes there are saved immediately.",
    },
    {
      selector: '[data-help="edit-client-dialog-save"]',
      section: CLIENT,
      context: "client-dialog",
      title: "Save the client settings",
      body: "Saves the client dialog and closes it. Disabled until you change something, and always disabled for read-only roles.",
    },
    // Implementations can only be ADDED from this page, so the edit-only step is left out.
    ...implementationDialogSteps.filter(
      (step) => step.context !== "implementation-dialog-edit"
    ),
    ...documentsDialogSteps,
  ],
};
